/**
 * 🔴 US-AIV2-0022 (@UC0161 @UC0162 · ADR-0009 "Người làm") — HÌNH DẠNG KẾT QUẢ của MỌI resolver ô.
 *
 * Người làm đổi CHỮ (span do Người phiên dịch gạch chân, hoặc chữ người dùng trả lời ô) thành GIÁ TRỊ có kiểu
 * (id thành viên · tiền `bigint` · id quỹ/đợt/nhóm · ngày · giá trị enum · chữ nguyên văn). Luật cốt lõi của
 * vai này (ADR-0009, cột "Cấm": *đoán (0 hoặc ≥2 khớp ⇒ hỏi)*) được ĐÚC VÀO KIỂU: chỉ có ba trạng thái —
 *
 *   · `resolved`   — đúng MỘT giá trị, kèm nhãn hiển thị (lấy từ sổ thật / Sổ năng lực, KHÔNG do LLM đặt);
 *   · `candidates` — ≥1 ứng viên để HỎI kèm lựa chọn (chip là sự kiện có cấu trúc — bấm = 0 LLM, @UC0174);
 *   · `none`       — không đọc ra được / phạm vi rỗng ⇒ hỏi tự do bằng câu `ask` của ô (0023 render).
 *
 * KHÔNG có trạng thái "resolved với độ chắc" — chọn ứng viên đầu khi ≥2 khớp là lỗi tiền, và test AC1 của task
 * (đột biến "chọn ứng viên đầu") đỏ đúng ở chỗ đó. Giá trị tiền LUÔN `bigint` minor (CLAUDE.md §3.1, G6).
 *
 * File này KHÔNG import contracts (chiều phụ thuộc contracts → domain); DTO trên dây (string minor cho tiền) là
 * việc của 0023 ở tầng api.
 */

/** Vì sao resolver không trả `resolved` — để 0023 chọn câu hỏi (clarify kèm chip · hỏi tự do · báo phạm vi rỗng). */
export const SLOT_RESOLUTION_REASON = Object.freeze({
  /** Có chữ, ≥2 mục khớp ⇒ hỏi kèm ĐÚNG các mục khớp (không chọn hộ). */
  AMBIGUOUS: "ambiguous",
  /** Có chữ nhưng không khớp mục nào trong phạm vi ⇒ hỏi lại kèm danh sách phạm vi. */
  UNMATCHED: "unmatched",
  /** Chưa có chữ (ô trống) ⇒ hỏi kèm lựa chọn (hoặc hỏi tự do khi loại ô không có danh sách). */
  MISSING: "missing",
  /** Phạm vi rỗng — không có gì để chọn (người chưa có quỹ · không thuộc nhóm · nhóm chưa có đợt thu). */
  EMPTY_SCOPE: "empty_scope",
  /**
   * 🔴 US-AIV2-0023d (@UC0226) — phạm vi rỗng vì MỌI quỹ thấy được đang **đóng băng**, không phải vì "chưa có".
   *
   * Hai thứ trông giống hệt nhau ở đầu ra (`0` ứng viên) nhưng người dùng phải làm hai việc KHÁC HẲN, nên gộp
   * chúng là dẫn người ta đi sửa sai chỗ: ca thật (#853) trả lời *"Chưa có đợt thu nào đang mở."* cho một nhóm
   * có quỹ, có đợt thu đang mở, chỉ là quỹ bị khoá ⇒ người dùng đi mở đợt thu / tạo quỹ thứ hai.
   * Lý do do LOADER đo (nó thấy `funds.status`); resolver thuần chỉ chuyển tiếp khi danh sách rỗng.
   */
  SCOPE_FUND_FROZEN: "scope_fund_frozen",
  /** Tiền: bậc chưa rõ mà KHÔNG tính được tập ứng viên tất định ("1 triệu 2", "rưỡi", "3 chục") ⇒ hỏi tự do. */
  AMBIGUOUS_MAGNITUDE: "ambiguous_magnitude",
  /** Không đọc ra được (ngày sai lịch, chữ không phải tiền, chữ rỗng sau khi cắt). */
  UNPARSABLE: "unparsable",
  /**
   * 🔴 US-AIV2-0033 (@UC0194) — ô `list` khai `duplicates: "ask"`: danh sách dán vào có mục LẶP ⇒ hỏi, KHÔNG gộp.
   *
   * Chỉ ô nghĩa "TÊN MỚI để TẠO" mới bật (mặc định `drop` giữ nguyên hành vi cũ). Với ô nghĩa "chọn trong số đang có"
   * thì gộp là đúng; với ô tạo suất thì gộp = **xoá mất một người thật**, và vì `createSeats` ghi `member_dues` cùng tx,
   * người biến mất kéo theo một nghĩa vụ tiền không bao giờ được sinh. Đây KHÔNG phải phân giải `memberId` (cam kết #4):
   * đó là **lặp-khi-TẠO**, không phải **tham chiếu mơ hồ tới người đang có** — hai thứ khác nhau, đừng mượn danh nhau.
   */
  DUPLICATE_ITEMS: "duplicate_items",
  /**
   * 🔴 US-AIV2-0033 vòng 2 — ô `list`: span của LLM cho **ÍT mục hơn** câu người dùng vừa dán ⇒ **trích xuất MỘT PHẦN**.
   * Không bao giờ im lặng nhận: hoặc dùng nguyên văn, hoặc HỎI LẠI. Ghi theo phần bị cắt = mất người + mất nghĩa vụ tiền.
   */
  PARTIAL_EXTRACT: "partial_extract",
  /**
   * 🔴 AI-INTERP C3 re-review (@UC0201 @UC0161 · sec M-2) — ô người OPT-IN `unnamedMeansBlank`: chữ chỉ CẢ NHÓM («mọi người chưa đóng») ⇒ người dùng chưa nói
   * gì về ô ⇒ Người làm bỏ ô như «Bỏ trống». Lý do RIÊNG, không mượn `missing`: `missing` của resolver khác (ô `list` toàn câu lệnh «thêm người mới vào nhóm»,
   * ô tiền…) nghĩa là «HỎI LẠI» — gộp hai nghĩa là biến «hỏi lại» thành «bỏ trống = tất cả / đủ số» ở ô tuỳ chọn.
   */
  UNSAID: "unsaid",
} as const);

export type SlotResolutionReason = (typeof SLOT_RESOLUTION_REASON)[keyof typeof SLOT_RESOLUTION_REASON];

/** Một ứng viên để hỏi: giá trị có kiểu + nhãn hiển thị (tên thật trong sổ / nhãn từ Sổ năng lực). */
export interface SlotCandidate<T> {
  readonly value: T;
  readonly label: string;
}

export type SlotResolution<T> =
  | { readonly status: "resolved"; readonly value: T; readonly label: string }
  | {
      readonly status: "candidates";
      readonly reason: SlotResolutionReason;
      /** ≥1 phần tử, thứ tự TẤT ĐỊNH (thứ tự sổ thật / thứ tự Sổ / QĐ sản phẩm) — không phải "gần đúng nhất trước". */
      readonly candidates: readonly SlotCandidate<T>[];
    }
  | {
      readonly status: "none";
      readonly reason: SlotResolutionReason;
      /**
       * Mảnh chữ gây mơ hồ — CẮT ≤ `SLOT_TOKEN_MAX_CHARS` ký tự (`slotNone` cắt), KHÔNG nguyên câu: 0023 có thể ghi nó vào
       * audit append-only, và câu trả lời ô có thể chứa SĐT (review #815 L2). Chỉ để câu hỏi lại có trọng tâm.
       */
      readonly token?: string;
    };

/** Giá trị mà một ô có thể mang sau khi resolve — tập đóng theo `AI_SLOT_TYPES`. */
export type SlotValue = string | bigint | readonly string[];

export function slotResolved<T>(value: T, label: string): SlotResolution<T> {
  return { status: "resolved", value, label };
}

/** Trần độ dài `token` trả ra ngoài — đủ để hỏi có trọng tâm, không đủ để rò một câu/SĐT. */
export const SLOT_TOKEN_MAX_CHARS = 32;

/**
 * `candidates` với 0 phần tử là LỖI LẬP TRÌNH của resolver (phạm vi rỗng phải được xử lý tường minh thành `none/empty_scope`
 * TRƯỚC khi gọi) — ném, không lặng lẽ đổi trạng thái rồi vứt `reason` của caller (review #815 L1).
 */
export function slotCandidates<T>(
  reason: SlotResolutionReason,
  items: readonly SlotCandidate<T>[],
): SlotResolution<T> {
  if (items.length === 0) throw new Error(`slotCandidates(${reason}) với 0 ứng viên — resolver phải trả none/empty_scope tường minh`);
  return { status: "candidates", reason, candidates: Object.freeze([...items]) };
}

export function slotNone<T>(reason: SlotResolutionReason, token?: string): SlotResolution<T> {
  const cut = token?.trim().slice(0, SLOT_TOKEN_MAX_CHARS);
  return cut === undefined || cut === "" ? { status: "none", reason } : { status: "none", reason, token: cut };
}

/**
 * 🔴 US-AIV2-0023d — TẬP ĐÓNG các lý do nghĩa là "không có gì để chọn".
 *
 * Engine (`AiDoerService.resolveAll`) có ĐÚNG MỘT nhánh cho tình huống này (huỷ phiếu, không dựng phiếu rỗng);
 * thêm một lý do rỗng mới mà quên thêm vào đây thì ô rơi xuống nhánh "hỏi lại" và người dùng bị hỏi một câu
 * không có câu trả lời nào đúng. Vì thế đây là DANH SÁCH, không phải `reason === EMPTY_SCOPE` rải khắp nơi.
 */
export const AI_EMPTY_SCOPE_REASONS: readonly SlotResolutionReason[] = Object.freeze([
  SLOT_RESOLUTION_REASON.EMPTY_SCOPE,
  SLOT_RESOLUTION_REASON.SCOPE_FUND_FROZEN,
]);

export function isEmptyScopeReason(reason: SlotResolutionReason): boolean {
  return AI_EMPTY_SCOPE_REASONS.includes(reason);
}
